import {
  CLASSIC_RULESET,
  type Coord,
  canPlace,
  isFleetLegal,
  mulberry32,
  type Orientation,
  randomFleet,
  type Ship,
  type ShipSize,
  shipCount,
} from '@sea-wars/game-core';
import { SketchBoard } from '@sea-wars/ui';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { track } from '../analytics.js';

const RULESET = CLASSIC_RULESET;
const STORAGE_KEY = 'sea-wars:last-fleet';
/** Próg między dwoma tapnięciami, żeby liczyły się jako double-tap (obrót), a nie dwa osobne chwyty. */
const DOUBLE_TAP_MS = 350;

/** Statki do rozstawienia, od najdłuższego — tak też łatwiej je układać. */
const ROSTER_ORDER: ShipSize[] = [4, 3, 3, 2, 2, 2, 1, 1, 1, 1];

interface Selection {
  readonly id: number;
  readonly size: ShipSize;
  readonly dir: Orientation;
  /** Które pole statku trzyma wskaźnik — bez tego statek „skacze" przy chwycie za rufę. */
  readonly grab: number;
}

export interface PlacementEditorProps {
  readonly disabled: boolean;
  /** Do zdarzeń produktowych (`placement_started`/`_done`/`_abandoned`) — patrz `docs/06` §6.3. */
  readonly gameId: string | undefined;
  readonly onConfirm: (fleet: Ship[]) => void;
}

/**
 * Edytor rozstawienia floty.
 *
 * Dwa sposoby stawiania, bo jeden nie wystarcza: przeciąganie (naturalne myszą)
 * oraz tap-wybierz → tap-postaw (jedyne sensowne na małym ekranie i jedyne dostępne
 * z klawiatury). Legalność liczy `game-core` — ta sama funkcja, którą serwer
 * odrzuci nielegalną flotę, więc podgląd nie może skłamać.
 */
export function PlacementEditor({ disabled, gameId, onConfirm }: PlacementEditorProps) {
  const [placed, setPlaced] = useState<Ship[]>([]);
  const [selection, setSelection] = useState<Selection | null>(null);
  const [hover, setHover] = useState<Coord | null>(null);
  const [dragging, setDragging] = useState(false);
  const [history, setHistory] = useState<Ship[][]>([]);

  const startedAtRef = useRef(Date.now());
  const confirmedRef = useRef(false);
  const placedCountRef = useRef(0);
  const usedRandomRef = useRef(false);
  const lastTapRef = useRef<{ id: number; time: number } | null>(null);
  placedCountRef.current = placed.length;

  useEffect(() => {
    track('placement_started', { gameId });
    return () => {
      if (!confirmedRef.current) {
        track('placement_abandoned', { gameId, props: { atShip: placedCountRef.current } });
      }
    };
  }, [gameId]);

  const remaining = useMemo(() => {
    const used = new Set(placed.map((ship) => ship.id));
    return ROSTER_ORDER.map((size, id) => ({ id, size })).filter((entry) => !used.has(entry.id));
  }, [placed]);

  const complete = placed.length === shipCount(RULESET.roster);
  const legal = complete && isFleetLegal(placed, RULESET);

  const remember = useCallback((next: Ship[]) => {
    setHistory((past) => [...past.slice(-19), next]);
  }, []);

  /** Statek „duch" pod wskaźnikiem — zielony, gdy da się postawić, czerwony gdy nie. */
  const ghost = useMemo(() => {
    if (!selection || !hover) return null;
    const anchor: Ship = {
      id: selection.id,
      size: selection.size,
      dir: selection.dir,
      x: selection.dir === 'h' ? hover.x - selection.grab : hover.x,
      y: selection.dir === 'v' ? hover.y - selection.grab : hover.y,
    };
    return { ship: anchor, legal: canPlace(placed, anchor, RULESET.boardSize) };
  }, [selection, hover, placed]);

  const commitGhost = useCallback(() => {
    if (!ghost?.legal) return false;
    remember(placed);
    usedRandomRef.current = false;
    setPlaced((current) => [...current.filter((s) => s.id !== ghost.ship.id), ghost.ship]);
    setSelection(null);
    setHover(null);
    return true;
  }, [ghost, placed, remember]);

  const rotate = useCallback(() => {
    setSelection((current) =>
      current ? { ...current, dir: current.dir === 'h' ? 'v' : 'h' } : current,
    );
  }, []);

  /**
   * Obrót statku, który już stoi na planszy, bez podnoszenia go — jedyny sposób na dotyku,
   * gdzie nie ma klawisza R (`docs/05-ux-styl-szkicu.md` §5.3). Dziób (`x`/`y`) zostaje na
   * miejscu, obraca się tylko ogon; jeśli obrócona wersja by kolidowała albo wystawała poza
   * planszę, nic się nie dzieje — `onPointerDown` wtedy spada do zwykłego chwytu za statek.
   */
  const rotateInPlace = useCallback(
    (ship: Ship): boolean => {
      const rotated: Ship = { ...ship, dir: ship.dir === 'h' ? 'v' : 'h' };
      if (!canPlace(placed, rotated, RULESET.boardSize)) return false;
      remember(placed);
      setPlaced((current) => current.map((s) => (s.id === ship.id ? rotated : s)));
      return true;
    },
    [placed, remember],
  );

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key.toLowerCase() === 'r') rotate();
      if (event.key === 'Escape') setSelection(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [rotate]);

  const pickFromDock = (entry: { id: number; size: ShipSize }) => {
    setSelection({ id: entry.id, size: entry.size, dir: 'h', grab: 0 });
  };

  const onPointerDown = (cell: Coord) => {
    const existing = placed.find((ship) =>
      ship.dir === 'h'
        ? cell.y === ship.y && cell.x >= ship.x && cell.x < ship.x + ship.size
        : cell.x === ship.x && cell.y >= ship.y && cell.y < ship.y + ship.size,
    );
    if (existing) {
      // Drugie szybkie tapnięcie tego samego statku (double-tap) obraca go w miejscu
      // zamiast go podnosić — patrz `rotateInPlace`.
      const now = Date.now();
      const lastTap = lastTapRef.current;
      const isDoubleTap = lastTap?.id === existing.id && now - lastTap.time < DOUBLE_TAP_MS;
      lastTapRef.current = isDoubleTap ? null : { id: existing.id, time: now };
      if (isDoubleTap && rotateInPlace(existing)) return;

      // Chwyt za już postawiony statek = przesuwanie go, nie stawianie nowego.
      remember(placed);
      setPlaced((current) => current.filter((ship) => ship.id !== existing.id));
      setSelection({
        id: existing.id,
        size: existing.size,
        dir: existing.dir,
        grab: existing.dir === 'h' ? cell.x - existing.x : cell.y - existing.y,
      });
      setHover(cell);
      setDragging(true);
      return;
    }
    lastTapRef.current = null;
    if (selection) {
      setHover(cell);
      setDragging(true);
    }
  };

  const onPointerUp = (cell: Coord | null) => {
    if (!dragging) return;
    setDragging(false);
    if (cell) setHover(cell);
    // Przeciągnięcie kończy się postawieniem; jeśli miejsce jest zajęte,
    // statek zostaje „w ręce" i można spróbować gdzie indziej.
    commitGhost();
  };

  const randomize = () => {
    remember(placed);
    const fleet = randomFleet(mulberry32(Date.now() & 0x7fffffff), RULESET);
    setPlaced(fleet.map((ship, index) => ({ ...ship, id: index })));
    setSelection(null);
    usedRandomRef.current = true;
  };

  const clear = () => {
    remember(placed);
    setPlaced([]);
    setSelection(null);
    usedRandomRef.current = false;
  };

  const undo = () => {
    setHistory((past) => {
      const previous = past.at(-1);
      if (previous) setPlaced(previous);
      return past.slice(0, -1);
    });
    setSelection(null);
    usedRandomRef.current = false;
  };

  const restoreLast = () => {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const fleet = JSON.parse(raw) as Ship[];
      if (isFleetLegal(fleet, RULESET)) {
        remember(placed);
        setPlaced(fleet);
        usedRandomRef.current = false;
      }
    } catch {
      // Zapisany układ to wygoda, nie dane krytyczne — cicha porażka jest tu w porządku.
    }
  };

  const confirm = () => {
    if (!legal) return;
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(placed));
    } catch {
      /* prywatne okno albo zablokowane dane witryny */
    }
    confirmedRef.current = true;
    track('placement_done', {
      gameId,
      props: { durationMs: Date.now() - startedAtRef.current, usedRandom: usedRandomRef.current },
    });
    onConfirm(placed);
  };

  const hasSaved = (() => {
    try {
      return Boolean(window.localStorage.getItem(STORAGE_KEY));
    } catch {
      return false;
    }
  })();

  return (
    <section className="placement" aria-label="Rozstawianie floty">
      <SketchBoard
        size={RULESET.boardSize}
        seed={4242}
        fleet={placed}
        ghost={ghost}
        interactive={!disabled}
        ariaLabel="Twoja plansza — rozstaw flotę"
        onCellActivate={(cell) => {
          if (!selection) return;
          setHover(cell);
          // Tapnięcie stawia natychmiast — na dotyku nie ma najechania kursorem.
          queueMicrotask(() => commitGhost());
        }}
        onCellHover={(cell) => !dragging && setHover(cell)}
        onCellPointerDown={onPointerDown}
        onCellPointerMove={(cell) => dragging && cell && setHover(cell)}
        onCellPointerUp={onPointerUp}
      />

      {/* biome-ignore lint/a11y/useSemanticElements: to pasek wyboru statków, a nie pola
          formularza — <fieldset> narzuciłby tu semantykę, której ta grupa nie ma */}
      <div className="dock" role="group" aria-label="Statki do rozstawienia">
        {remaining.map((entry) => (
          <button
            key={entry.id}
            type="button"
            className={`dock__ship${selection?.id === entry.id ? ' dock__ship--active' : ''}`}
            aria-pressed={selection?.id === entry.id}
            onClick={() => pickFromDock(entry)}
            disabled={disabled}
          >
            {'▪'.repeat(entry.size)}
          </button>
        ))}
        {remaining.length === 0 && <span className="dock__done">Cała flota na planszy</span>}
      </div>

      <div className="placement__controls">
        <button type="button" className="btn" onClick={rotate} disabled={!selection}>
          Obróć (R)
        </button>
        <button type="button" className="btn" onClick={randomize} disabled={disabled}>
          Losuj
        </button>
        <button type="button" className="btn" onClick={undo} disabled={history.length === 0}>
          Cofnij
        </button>
        <button type="button" className="btn" onClick={clear} disabled={placed.length === 0}>
          Wyczyść
        </button>
        {hasSaved && (
          <button type="button" className="btn" onClick={restoreLast} disabled={disabled}>
            Jak ostatnio
          </button>
        )}
        <button
          type="button"
          className="btn btn--primary"
          onClick={confirm}
          disabled={!legal || disabled}
        >
          Gotów
        </button>
      </div>

      <p className="placement__hint">
        {selection
          ? 'Wskaż pole na planszy. Obrót klawiszem R.'
          : 'Wybierz statek z listy albo przeciągnij już postawiony, żeby go przesunąć. Podwójne tapnięcie/kliknięcie obraca postawiony statek. Statki nie mogą się stykać — nawet rogami.'}
      </p>
    </section>
  );
}
